import {
  CreateJobResponseSchema,
  type CompanyDetail,
  type CompanyListRow,
  type ConfirmJobRequest,
  type ContactEmploymentRow,
  type ContactRow,
  type OutreachFeedback,
  type CreateJobResponse,
  type EnrichRequest,
  type ExportFormat,
  type ExportProfile,
  type JobCompanyRow,
  type JobSettings,
  type JobSummary,
  type MeResponse,
  type ProductStats,
  type ProviderId,
  type SavedView,
  type ProvidersResponse,
  type ValidationReport,
  type AuditEvent,
  type AuditListFilters,
  type RepairRecord,
  type RepairReviewRequest,
  type RepairStatus,
  type CompanyResearchRequest,
  type CompanyResearchResponse,
  type FromZohoJobRequest,
  type ZohoImportRequest,
  type ZohoImportRun,
  type ZohoModule,
  type ZohoSettings,
  type ZohoSettingsPatch,
  type ZohoSpamRecord,
  type ZohoStatusResponse,
  type ZohoViewsResponse,
  type InviteUsersResponse,
  type ResendInviteResponse,
  type UserChange,
  type UsersResponse,
} from '@profiler/shared'

/**
 * The one place that talks to the API.
 *
 * @profiler/shared fixes the payload shapes. The response *envelopes* are not in
 * the contract, so they are declared here against what backend/src/api/index.ts
 * actually returns — which is not uniform: some routes wrap in `{ job }`, some
 * return the record bare, and every list route returns a `Page`. Guessing a
 * uniform envelope is what made the first draft of this file wrong on ten
 * routes, so each method below mirrors one handler and nothing is inferred.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * A 401 is the end of the session: the ID token expired, or the pool rejected
 * it because the account was disabled or its tokens revoked.
 *
 * 403 is deliberately not this. That is an admin-only route refusing THIS
 * account, which is a perfectly good session being told no — signing it out
 * would throw away everything else it can still do.
 */
export function isSessionExpired(e: unknown): boolean {
  return e instanceof ApiError && e.status === 401
}

/**
 * The provider switch is refused with 409 while a job is running.
 *
 * That is a rule rather than a fault, and the person who pressed the button can
 * fix it themselves, so it gets a sentence that says what to do instead of the
 * red panel every other failure gets.
 */
export const PROVIDER_SWITCH_CONFLICT =
  'Let the running job finish, or pause or cancel it, then switch. A job that changed provider half way through would carry rows found by two different ladders at two different costs.'

export function isConflict(e: unknown): boolean {
  return e instanceof ApiError && e.status === 409
}

// ---------- Shapes the contract does not define -----------------------------

/**
 * Every list route answers with this. Cursors are opaque and a job may hold a
 * thousand companies, so the client follows them rather than showing page one
 * and quietly pretending it is the whole list.
 */
export interface Page<T> {
  items: T[]
  cursor: string | null
  /** The order the server actually applied, when it says. */
  order?: { sort: 'added' | 'updated'; dir: 'asc' | 'desc' }
}

/**
 * A review queue item. Mirrors ReviewItem in backend/src/lib/repos.ts, which
 * lives in a package the browser cannot import; the two must be changed together.
 *
 * The field names are the *stored* ones, taken from the single writer
 * (backend/src/workers/companyWorker.ts) rather than from what a review screen
 * would like them to be called. `accountName` is the company as the uploaded
 * sheet spelled it and `fullName` is the person — naming them `companyName` and
 * `personName` here type-checks against nothing and renders every card blank.
 */
export interface ReviewItem {
  jobId: string
  /** `rowKey#linkedinUrl` — the same key the employment row uses, so they join. */
  sortKey: string
  linkedinUrl: string
  /** The company as the uploaded sheet spelled it, not a resolved name. */
  accountName: string | null
  fullName: string | null
  title: string | null
  reasons: string[]
  notes: string[]
  confidence: number
  createdAt: string
  /** Defaulted by the repo on write, so always present on a stored item. */
  rowKey: string
  status: 'open' | 'accepted' | 'rejected'
  /** `ai:<model id>` when the automated reviewer decided it, never a person's email. */
  decidedBy: string | null
  decidedAt: string | null
  /**
   * What the automated reviewer advised, kept whether or not it was applied and
   * whether or not a human later overruled it — which is the only way anyone can
   * ask later whether the model was worth trusting.
   */
  aiVerdict?: 'accept' | 'reject' | 'escalate' | null
  aiReason?: string | null
  aiConfidence?: number | null
  aiModel?: string | null
  aiAt?: string | null
  /**
   * What the person's own profile says about their employer, joined by the list
   * endpoint. Without it "company mismatch" names a problem without showing it,
   * and the reviewer cannot tell a different legal name from a wrong person.
   */
  employment?: {
    companyName: string | null
    companyDomain: string | null
    companyLinkedinUrl: string | null
    title: string | null
    seniorityBand: string | null
    matchBasis: string | null
  } | null
  /**
   * Declared on the backend record but never written by the worker that creates
   * these, so absent on every real item. Optional rather than `| null`: the
   * difference is what stops the UI rendering an "Unknown" chip for a value that
   * was never a value.
   */
  companyDomain?: string | null
  matchBasis?: string
}

/**
 * Identity and employment are separate rows (plan section 6, so a founder of two
 * companies in one list does not collide on one key). The job contacts route
 * joins them; `contact` is the nullable half, because the employment row is what
 * the query walked and its identity row can lag behind by one write.
 */
export interface JobContactItem {
  employment: ContactEmploymentRow
  contact: ContactRow | null
}

/**
 * A decided review item, and what the decision did beyond this row.
 *
 * Only a rejection carries `closedElsewhere`: rejecting a person closes the
 * open rows for that same person at that same company in other jobs, and the
 * count is how many. Accept answers with the item bare, which is why the field
 * is optional rather than a number that is sometimes zero.
 */
export interface DecidedReview extends ReviewItem {
  closedElsewhere?: number
}

export interface ContactDetail {
  contact: ContactRow
  /** Every company this person has been matched to. */
  employment: ContactEmploymentRow[]
}

export type EnrichField = EnrichRequest['fields'][number]

/**
 * Re-exported from the schema that enforces it, never restated.
 *
 * This used to be a hand-copied 200 sitting beside a zod `.max(200)`, which is
 * two copies of a number that must agree — and the copy the dialog reads is the
 * one that decides whether a user is offered a button that can only fail.
 */
export { ENRICH_MAX_URLS } from '@profiler/shared'

export interface ContactListFilters {
  q?: string
  company?: string
  seniority?: string
  gate?: string
  outreach?: string
  workEmail?: string
  /** A confidence grade, or `safe` for the two an address may be mailed under. */
  grade?: string
  country?: string
  phone?: string
  /** `zoho` for people the CRM holds, `none` for people it does not. */
  crm?: string
  /** `added` (default) or `updated`, with `asc` or `desc` (default). */
  sort?: string
  dir?: string
}

export interface ReviewListFilters {
  status?: string
  jobId?: string
  q?: string
  reason?: string
  aiVerdict?: string
  decidedBy?: string
}

export interface CompanyListFilters {
  q?: string
  /** A triage class, or `none` for a website never checked. */
  domainClass?: string
  /** One of COMPANY_PEOPLE_FILTERS. */
  people?: string
  /** One of COMPANY_LINKEDIN_FILTERS. */
  linkedin?: string
  outcome?: string
  /** One of COMPANY_REPAIR_FILTERS. */
  repair?: string
  /** One of COMPANY_CRM_FILTERS: `zoho` for companies that are Zoho CRM Accounts, `none` for the rest. */
  crm?: string
}

export interface OutcomeImportResult {
  rows: number
  applied: number
  matched: number
  /** Addresses with no contact behind them. Named, not just counted. */
  unmatched: string[]
  /** Rows whose outcome word was not recognised, rather than guessed at. */
  unreadable: string[]
  /**
   * How each outcome found its company: stated in the file, inferred from the
   * address's domain, or fallen back to the person's newest. Reported so a
   * report built mostly on the last one says so on its face.
   */
  attribution: Record<string, number>
  encoding: string
}

export interface DeletedJob {
  jobId: string
  removed: { companies: number; reviews: number; contacts: number }
}

export interface AutoReviewResult {
  /**
   * Open items the reviewer had already judged, and so skipped.
   *
   * The difference between "there was nothing to do" and "it did not run",
   * which a `reviewed: 0` on its own cannot express.
   */
  alreadySeen?: number
  reviewed: number
  accepted: number
  rejected: number
  /** Left open for a person, which is the reviewer's default answer. */
  escalated: number
  /** True when the queue held more than one batch. */
  remaining: boolean
}

export interface EnrichResponse {
  requested: number
  /** Already held, or already pending: those cost nothing and are not re-bought. */
  skipped: Array<{ linkedinUrl: string; reason: string }>
}

export type ContactPatch = Partial<Pick<ContactRow, 'outreachStatus' | 'notes'>>

export type ExportStatus = 'pending' | 'running' | 'ready' | 'failed'

export interface ExportRecord {
  exportId: string
  jobId: string
  format: ExportFormat
  profile: ExportProfile
  status: ExportStatus
  error: string | null
  rowCount: number | null
  /**
   * An export by filters that matched more people than one file may carry. The
   * file was built from the first EXPORT_MAX_MATCHING, in the order the list showed.
   */
  truncated?: boolean
  requestedBy: string
  requestedAt: string
  completedAt: string | null
  /** Presigned, five minutes. Null until the workbook exists. */
  downloadUrl?: string | null
}

/**
 * Exactly one scope. A job export audits an uploaded list and keeps a row for
 * every input company; a contacts export is a hand-picked set of people; an
 * export by filters is everyone the Contacts list's filters match, found by the
 * server rather than listed here.
 */
export type CreateExportRequest = {
  format: ExportFormat
  profile: ExportProfile
  onlyWithEmail: boolean
  excludeDoNotContact: boolean
} & (
  | { jobId: string; linkedinUrls?: never; filters?: never }
  | { linkedinUrls: string[]; jobId?: never; filters?: never }
  | { filters: ContactListFilters; jobId?: never; linkedinUrls?: never }
)

export interface RerunCompanyRequest {
  website?: string
  linkedinUrl?: string
}

/** Mirrors AppSettingsSchema in backend/src/lib/repos.ts. */
export interface AppSettings {
  killSwitch: boolean
  rateLimitPerMinute: number
  monthlyCreditCap: number
  /** Measured in calibration. The estimator reads these, never constants. */
  measuredCosts: Record<string, { base: number; perResult: number }>
  /**
   * Which provider these costs were measured against.
   *
   * Optional, and rendered only when present: an endpoint name means a different
   * thing on each provider, and a table labelled with the wrong one is worse
   * than a table labelled with none.
   */
  measuredCostsProvider?: ProviderId
  yieldDistribution: Record<string, number>
  defaultJobSettings: Partial<JobSettings>
  alertEmail: string | null
  creditBalance: number | null
  creditBalanceAt: string | null
  updatedAt: string | null
  updatedBy: string | null
  /** The Zoho CRM block, read here and edited through its own routes. */
  zoho?: ZohoSettings
}

/** What `POST /api/zoho/push` answers: who was queued, and who was not and why. */
export interface ZohoPushResponse {
  queued: number
  skipped: Array<{ linkedinUrl: string; reason: string }>
}

export type AppSettingsPatch = Partial<
  Pick<
    AppSettings,
    'killSwitch' | 'rateLimitPerMinute' | 'monthlyCreditCap' | 'defaultJobSettings' | 'alertEmail'
  >
>

export interface CreditsUsage {
  month: { key: string; used: number; cap: number }
  days: Array<{ day: string; used: number }>
  providerBalance: number | null
  providerBalanceAt: string | null
  killSwitch: boolean
}

export interface ConfirmJobResponse {
  job: JobSummary
  validation: ValidationReport
}

// ---------- Client ----------------------------------------------------------

export interface ApiClientOptions {
  baseUrl: string
  /**
   * The Cognito **ID** token. The access token is not usable here: it carries
   * neither `aud` nor `email`, and the JWT authorizer checks both.
   */
  getToken: () => string | undefined
}

type QueryValue = string | number | boolean | undefined

function queryString(params: Record<string, QueryValue>): string {
  const sp = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue
    sp.set(key, String(value))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

/**
 * rowKey, a review sort key (`rowKey#linkedinUrl`) and a contact id are all
 * structural in a path. The handler splits the raw path on `/` and only then
 * percent-decodes each segment, so encoding here keeps a URL in one segment.
 */
function seg(value: string): string {
  return encodeURIComponent(value)
}

/**
 * The contact id segment. The handler accepts either a full profile URL or the
 * bare public slug, and the slug survives any proxy that normalises `%2F` on the
 * way in — so send the slug whenever the URL is in canonical form.
 */
function contactSeg(linkedinUrl: string): string {
  const match = /linkedin\.com\/in\/([^/?#]+)/i.exec(linkedinUrl)
  return seg(match?.[1] ?? linkedinUrl)
}

/**
 * How many pages a full-list read will follow before giving up.
 *
 * This was derived from the size of one JOB, which is the wrong ceiling: the
 * contacts and companies screens list everything across ALL jobs. At 200 rows a
 * page that came to 1,400 rows — and with 1,116 contacts and 1,203 companies
 * already stored, the next batch of a thousand would have silently cut both
 * lists off mid-way, with a total and a filter count that looked authoritative
 * and were wrong.
 *
 * Thirty pages of a thousand is thirty thousand rows, comfortably past any list
 * this product will hold before it needs paging in the UI rather than a bigger
 * number here. And hitting the bound is no longer silent: `collectPage` reports
 * it so a screen can say so.
 */
const MAX_PAGES = 30

/**
 * The page size for `collect`, which deliberately fetches EVERYTHING.
 *
 * Only for the callers that genuinely need every row — an export, or the option
 * list behind a filter. It is not what a screen asks for: a list showing
 * twenty-five rows used to request a thousand and paginate them in the browser,
 * which is a thousand-row download with a viewer on top.
 */
const COLLECT_PAGE_SIZE = 1000

/**
 * What a list asks for when the caller does not say.
 *
 * Matches the grid's own default page size, so the request and what appears are
 * the same rows.
 */
export const DEFAULT_PAGE_SIZE = 25

export class ApiClient {
  constructor(private readonly options: ApiClientOptions) {}

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers)
    const token = this.options.getToken()
    if (token) headers.set('Authorization', `Bearer ${token}`)
    headers.set('Accept', 'application/json')

    // FormData must set its own Content-Type: the multipart boundary is
    // generated per body, and an inherited application/json would leave the
    // server's parser with no boundary to split on.
    if (init.body !== undefined && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }

    let res: Response
    try {
      res = await fetch(`${this.options.baseUrl}${path}`, { ...init, headers })
    } catch (cause) {
      throw new ApiError(0, 'Could not reach the API. Check your connection and try again.', cause)
    }

    if (res.status === 204) return undefined as T

    const text = await res.text()
    let body: unknown = undefined
    if (text) {
      try {
        body = JSON.parse(text)
      } catch {
        body = text
      }
    }

    if (!res.ok) throw new ApiError(res.status, errorMessage(res.status, body), body)
    return body as T
  }

  /**
   * Download a file the API generates, as the signed-in user.
   *
   * A plain link cannot be used: every API route sits behind the JWT authorizer,
   * and an anchor sends no Authorization header. So fetch it, then hand the
   * browser a blob to save.
   */
  async downloadFile(path: string, fallbackName: string): Promise<void> {
    const headers = new Headers()
    const token = this.options.getToken()
    if (token) headers.set('Authorization', `Bearer ${token}`)

    let res: Response
    try {
      res = await fetch(`${this.options.baseUrl}${path}`, { headers })
    } catch (cause) {
      throw new ApiError(0, 'Could not reach the API. Check your connection and try again.', cause)
    }
    if (!res.ok) throw new ApiError(res.status, errorMessage(res.status, await res.text()))

    // Prefer the server's filename; it carries the right extension for the format.
    const disposition = res.headers.get('content-disposition') ?? ''
    const named = /filename="([^"]+)"/.exec(disposition)?.[1]

    const blob = await res.blob()
    const href = URL.createObjectURL(blob)
    try {
      const a = document.createElement('a')
      a.href = href
      a.download = named ?? fallbackName
      document.body.appendChild(a)
      a.click()
      a.remove()
    } finally {
      // Revoking immediately can cancel the download in Safari, so give it a tick.
      setTimeout(() => URL.revokeObjectURL(href), 10_000)
    }
  }

  /** The sample upload file, generated server-side so it cannot drift. */
  async downloadTemplate(format: 'xlsx' | 'csv' = 'xlsx'): Promise<void> {
    await this.downloadFile(`/api/template?format=${format}`, `company-profiler-sample.${format}`)
  }

  /**
   * Follow every cursor. The grids sort, filter and total across the whole list,
   * and a client-side total computed from page one is wrong in a way nobody can
   * see — which is worse than being slow.
   */
  private async collect<T>(path: string, query: Record<string, QueryValue> = {}): Promise<T[]> {
    return (await this.collectPage<T>(path, query)).items
  }

  /**
   * Follow every cursor, and say whether we reached the end.
   *
   * Stopping at the bound used to be indistinguishable from reaching the end of
   * the data, which is the worst way to be wrong here: the grid still shows a
   * total, the filters still show counts, and every one of them is confidently
   * short. A caller that can render "showing the first N" gets `truncated`.
   */
  protected async collectPage<T>(
    path: string,
    query: Record<string, QueryValue> = {},
  ): Promise<{ items: T[]; truncated: boolean }> {
    const items: T[] = []
    let cursor: string | null = null
    for (let page = 0; page < MAX_PAGES; page++) {
      const result: Page<T> = await this.request<Page<T>>(
        `${path}${queryString({ ...query, limit: COLLECT_PAGE_SIZE, cursor: cursor ?? undefined })}`,
      )
      items.push(...result.items)
      cursor = result.cursor
      if (!cursor) return { items, truncated: false }
    }
    return { items, truncated: true }
  }

  // -- identity, settings, credits --

  me(): Promise<MeResponse> {
    return this.request<MeResponse>('/api/me')
  }

  /** This person's saved views, across every list screen. */
  savedViews(): Promise<{ views: SavedView[] }> {
    return this.request<{ views: SavedView[] }>('/api/views')
  }

  /** The whole list, replaced — saving, renaming and deleting are all this. */
  putSavedViews(views: readonly SavedView[]): Promise<{ views: SavedView[] }> {
    return this.request<{ views: SavedView[] }>('/api/views', {
      method: 'PUT',
      body: JSON.stringify({ views }),
    })
  }

  /**
   * The dashboard, as one read.
   *
   * Deliberately one endpoint rather than a tile-per-request fan-out: the
   * figures are one snapshot taken at one moment, and fetching them separately
   * would let the tiles disagree with each other on screen.
   */
  dashboard(): Promise<ProductStats> {
    return this.request<ProductStats>('/api/dashboard')
  }

  settings(): Promise<AppSettings> {
    return this.request<AppSettings>('/api/settings')
  }

  updateSettings(patch: AppSettingsPatch): Promise<AppSettings> {
    return this.request<AppSettings>('/api/settings', { method: 'PUT', body: JSON.stringify(patch) })
  }

  creditsUsage(): Promise<CreditsUsage> {
    return this.request<CreditsUsage>('/api/credits/usage')
  }

  /**
   * Make one provider active. Admin only, and 409 while any job is running —
   * see PROVIDER_SWITCH_CONFLICT for how that is shown.
   */
  setActiveProvider(providerId: ProviderId): Promise<ProvidersResponse> {
    return this.request<ProvidersResponse>('/api/settings/provider', {
      method: 'PUT',
      body: JSON.stringify({ providerId }),
    })
  }

  // -- jobs --

  listJobs(): Promise<JobSummary[]> {
    return this.collect<JobSummary>('/api/jobs')
  }

  /** Returns the job bare, not wrapped: `json(200, await requireJob(...))`. */
  getJob(jobId: string): Promise<JobSummary> {
    return this.request<JobSummary>(`/api/jobs/${seg(jobId)}`)
  }

  /** Multipart upload. The response carries the detected encoding and headers. */
  async createJob(file: File, name?: string): Promise<CreateJobResponse> {
    const form = new FormData()
    form.append('file', file, file.name)
    if (name) form.append('name', name)
    const body = await this.request<unknown>('/api/jobs', { method: 'POST', body: form })
    // Parsed rather than cast: this is the one response the wizard builds a
    // whole multi-step form from, and a missing suggestedMapping should fail
    // here rather than three steps later as an undefined lookup.
    return CreateJobResponseSchema.parse(body)
  }

  /**
   * Confirm the mapping and settings. There is no separate validate route: this
   * handler is what runs the validator, and it returns the report alongside the
   * job. It is safe to call repeatedly while the job is DRAFT or READY — it
   * rewrites the staged company rows and spends nothing — which is what lets the
   * wizard's review step show a live report before anyone presses Start.
   */
  confirmJob(jobId: string, req: ConfirmJobRequest): Promise<ConfirmJobResponse> {
    return this.request<ConfirmJobResponse>(`/api/jobs/${seg(jobId)}`, {
      method: 'PUT',
      body: JSON.stringify(req),
    })
  }

  startJob(jobId: string): Promise<JobSummary> {
    return this.request<{ job: JobSummary; queued: number }>(`/api/jobs/${seg(jobId)}/start`, {
      method: 'POST',
    }).then((r) => r.job)
  }

  pauseJob(jobId: string): Promise<JobSummary> {
    return this.request<{ job: JobSummary }>(`/api/jobs/${seg(jobId)}/pause`, { method: 'POST' }).then(
      (r) => r.job,
    )
  }

  resumeJob(jobId: string): Promise<JobSummary> {
    return this.request<{ job: JobSummary; queued: number }>(`/api/jobs/${seg(jobId)}/resume`, {
      method: 'POST',
    }).then((r) => r.job)
  }

  cancelJob(jobId: string): Promise<JobSummary> {
    return this.request<{ job: JobSummary }>(`/api/jobs/${seg(jobId)}/cancel`, { method: 'POST' }).then(
      (r) => r.job,
    )
  }

  /**
   * Import what the mail actually did.
   *
   * Sent as the file the platform exported, unmodified. The server reads the
   * columns and reports what it could not place rather than asking for a mapping
   * screen for a two-column file.
   */
  async importOutcomes(file: File): Promise<OutcomeImportResult> {
    const form = new FormData()
    form.append('file', file)
    return this.request<OutcomeImportResult>('/api/outreach/outcomes', { method: 'POST', body: form })
  }

  outreachFeedback(): Promise<OutreachFeedback> {
    return this.request<OutreachFeedback>('/api/outreach/feedback')
  }

  /** Only a job that never bought anything; the server enforces which. */
  deleteJob(jobId: string): Promise<DeletedJob> {
    return this.request<DeletedJob>(`/api/jobs/${seg(jobId)}`, { method: 'DELETE' })
  }

  retryFailed(jobId: string): Promise<{ retried: number; queued: number }> {
    return this.request<{ retried: number; queued: number }>(`/api/jobs/${seg(jobId)}/retry-failed`, {
      method: 'POST',
    })
  }

  // -- companies --

  /**
   * One page of a job's companies.
   *
   * It used to collect every row — up to thirty thousand — and paginate them in
   * the browser, and it did that again on every poll while the job was live. A
   * thousand-company job re-fetched a thousand rows every ten seconds to show
   * twenty-five of them.
   */
  listCompanies(
    jobId: string,
    cursor: string | null = null,
    limit: number = DEFAULT_PAGE_SIZE,
    status?: string,
  ): Promise<Page<JobCompanyRow>> {
    return this.request<Page<JobCompanyRow>>(
      `/api/jobs/${seg(jobId)}/companies${queryString({ limit, cursor: cursor ?? undefined, status })}`,
    )
  }

  /** Returns the claimed row bare, with 202: the work happens on the queue. */
  rerunCompany(jobId: string, rowKey: string, req: RerunCompanyRequest): Promise<JobCompanyRow> {
    return this.request<JobCompanyRow>(`/api/jobs/${seg(jobId)}/companies/${seg(rowKey)}/rerun`, {
      method: 'POST',
      body: JSON.stringify(req),
    })
  }

  // -- contacts --

  /** One page of a job's contacts, with the review filter served. */
  listJobContacts(
    jobId: string,
    cursor: string | null = null,
    limit: number = DEFAULT_PAGE_SIZE,
    gate?: string,
  ): Promise<Page<JobContactItem>> {
    return this.request<Page<JobContactItem>>(
      `/api/jobs/${seg(jobId)}/contacts${queryString({ limit, cursor: cursor ?? undefined, gate })}`,
    )
  }

  /**
   * Every contact in one job, followed to the end.
   *
   * Bounded by the product, not by hope: a job holds at most
   * MAX_COMPANIES_PER_JOB companies and yields on the order of one contact each,
   * so this is a couple of requests rather than an open-ended walk.
   *
   * It exists because the Contacts screen filters a job's rows in the BROWSER —
   * on company, seniority, email state, phone state, country, free text — and
   * none of those can be pushed to a per-job endpoint. Filtering one server page
   * of twenty-five would hide rows the server had matched while the pager went
   * on offering a next one, which is precisely the defect this codebase spent a
   * day removing from the other lists. Either the filter is served or the data
   * is whole; this is the second.
   */
  collectJobContacts(jobId: string): Promise<{ items: JobContactItem[]; truncated: boolean }> {
    return this.collectPage<JobContactItem>(`/api/jobs/${seg(jobId)}/contacts`)
  }

  /**
   * The global contact list. The route takes no filters, so the screen filters
   * what it has; the list is bounded by the contact database, not by a job.
   */
  /**
   * One page of contacts, filtered by the server.
   *
   * The filters travel to the database rather than being applied here. This
   * screen used to download every contact so it could filter in memory, which
   * is why it could not page and silently truncated once the list outgrew the
   * read.
   */
  listContacts(
    filters: ContactListFilters = {},
    cursor: string | null = null,
    limit: number = DEFAULT_PAGE_SIZE,
  ): Promise<Page<ContactRow>> {
    return this.request<Page<ContactRow>>(
      `/api/contacts${queryString({ ...filters, limit, cursor: cursor ?? undefined })}`,
    )
  }

  /**
   * Every company we have ever profiled, across all jobs.
   *
   * One row per company rather than per job row: a company in four uploads is
   * one company that has been profiled four times, which is the question this
   * list exists to answer.
   */
  listAllCompanies(
    filters: CompanyListFilters = {},
    cursor: string | null = null,
    limit: number = DEFAULT_PAGE_SIZE,
  ): Promise<Page<CompanyListRow>> {
    return this.request<Page<CompanyListRow>>(
      `/api/companies${queryString({ ...filters, limit, cursor: cursor ?? undefined })}`,
    )
  }

  getCompany(domain: string): Promise<CompanyDetail> {
    return this.request<CompanyDetail>(`/api/companies/${seg(domain)}`)
  }

  getContact(linkedinUrl: string): Promise<ContactDetail> {
    return this.request<ContactDetail>(`/api/contacts/${contactSeg(linkedinUrl)}`)
  }

  /** Only outreach status and notes are editable; everything else is provenance. */
  patchContact(linkedinUrl: string, patch: ContactPatch): Promise<ContactRow> {
    return this.request<ContactRow>(`/api/contacts/${contactSeg(linkedinUrl)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    })
  }

  /** Send selected companies back through discovery. */
  researchCompanies(req: CompanyResearchRequest): Promise<CompanyResearchResponse> {
    return this.request<CompanyResearchResponse>('/api/companies/research', {
      method: 'POST',
      body: JSON.stringify(req),
    })
  }

  enrich(req: EnrichRequest): Promise<EnrichResponse> {
    return this.request<EnrichResponse>('/api/contacts/enrich', {
      method: 'POST',
      body: JSON.stringify(req),
    })
  }

  // -- Zoho CRM --

  /** Administrators only: the block, the day's spend and the last import. */
  zohoStatus(): Promise<ZohoStatusResponse> {
    return this.request<ZohoStatusResponse>('/api/zoho/status')
  }

  /** Merged over the stored block; worker-stamped fields are refused by the server. */
  updateZohoSettings(patch: ZohoSettingsPatch): Promise<ZohoSettings> {
    return this.request<ZohoSettings>('/api/zoho/settings', { method: 'PUT', body: JSON.stringify(patch) })
  }

  /** Queues the handshake; the outcome lands in the block a few seconds later. */
  zohoConnect(dc?: ZohoSettings['dc']): Promise<{ queued: boolean }> {
    return this.request<{ queued: boolean }>('/api/zoho/connect', {
      method: 'POST',
      body: JSON.stringify(dc ? { dc } : {}),
    })
  }

  zohoDisconnect(): Promise<{ queued: boolean }> {
    return this.request<{ queued: boolean }>('/api/zoho/disconnect', { method: 'POST', body: '{}' })
  }

  startZohoImport(req: ZohoImportRequest): Promise<{ runId: string }> {
    return this.request<{ runId: string }>('/api/zoho/import', { method: 'POST', body: JSON.stringify(req) })
  }

  /** The latest run, or one by id. `{ run: null }` when there has never been one. */
  zohoImportRun(runId?: string): Promise<{ run: ZohoImportRun | null }> {
    return this.request<{ run: ZohoImportRun | null }>(
      runId ? `/api/zoho/import/${seg(runId)}` : '/api/zoho/import',
    )
  }

  /** Administrators only: CRM records held back as web-form spam, newest first. */
  zohoSpam(): Promise<{ items: ZohoSpamRecord[] }> {
    return this.request<{ items: ZohoSpamRecord[] }>('/api/zoho/spam')
  }

  /** Readable by anybody who can start a job. */
  zohoViews(module: ZohoModule = 'Accounts'): Promise<ZohoViewsResponse> {
    return this.request<ZohoViewsResponse>(`/api/zoho/views${queryString({ module })}`)
  }

  /** A job whose rows come from a Zoho CRM view instead of a file. */
  async createJobFromZoho(req: FromZohoJobRequest): Promise<CreateJobResponse> {
    const body = await this.request<unknown>('/api/jobs/from-zoho', {
      method: 'POST',
      body: JSON.stringify(req),
    })
    return CreateJobResponseSchema.parse(body)
  }

  /** Push accepted people to the CRM now, whatever the account's push mode. */
  pushToZoho(linkedinUrls: string[]): Promise<ZohoPushResponse> {
    return this.request<ZohoPushResponse>('/api/zoho/push', {
      method: 'POST',
      body: JSON.stringify({ linkedinUrls }),
    })
  }

  // -- reviews --

  /**
   * The audit trail, newest first.
   *
   * Administrators only on the server, so a non-admin never has a link to this
   * and would get a 403 if they typed one.
   */
  listAudit(
    filters: AuditListFilters = {},
    cursor: string | null = null,
    limit: number = DEFAULT_PAGE_SIZE,
  ): Promise<Page<AuditEvent>> {
    // `targetId` travels as `target`: the handler reads a short name and the
    // filter object uses the long one, so the rename happens here rather than
    // being a difference two files have to remember.
    const { targetId, ...rest } = filters
    return this.request<Page<AuditEvent>>(
      `/api/audit${queryString({ ...rest, target: targetId, limit, cursor: cursor ?? undefined })}`,
    )
  }

  // -- users --

  /**
   * Everybody who has signed in, with the rules for each already applied.
   *
   * Administrators only on the server. Each row says which changes are refused
   * and why, so the screen offers exactly what the API will accept.
   */
  listUsers(): Promise<UsersResponse> {
    return this.request<UsersResponse>('/api/users')
  }

  /** One change to one person. Answers the whole list again, because a change can move the rules for everyone else. */
  changeUser(sub: string, change: UserChange): Promise<UsersResponse> {
    return this.request<UsersResponse>(`/api/users/${seg(sub)}/${change}`, { method: 'POST', body: '{}' })
  }

  /** Invite addresses. All or nothing: one address that cannot be invited refuses the lot, and says which. */
  inviteUsers(emails: readonly string[], sendEmail = false): Promise<InviteUsersResponse> {
    return this.request<InviteUsersResponse>('/api/users/invites', {
      method: 'POST',
      body: JSON.stringify({ emails, sendEmail }),
    })
  }

  /** Email a pending invitation's sign-in link again. */
  resendInvite(email: string): Promise<ResendInviteResponse> {
    return this.request<ResendInviteResponse>(`/api/users/invites/${seg(email)}/email`, { method: 'POST' })
  }

  /** Withdraw an invitation nobody has used yet. */
  cancelInvite(email: string): Promise<UsersResponse> {
    return this.request<UsersResponse>(`/api/users/invites/${seg(email)}`, { method: 'DELETE' })
  }

  // -- repairs --

  /** One page of the repair ladder's records in one status — the queue, by default. */
  listRepairs(
    status: RepairStatus,
    cursor: string | null = null,
    limit: number = DEFAULT_PAGE_SIZE,
  ): Promise<Page<RepairRecord>> {
    return this.request<Page<RepairRecord>>(
      `/api/repairs${queryString({ status, limit, cursor: cursor ?? undefined })}`,
    )
  }

  /** 404 when the ladder never examined the company — most companies, and not an error. */
  getRepair(domain: string): Promise<RepairRecord> {
    return this.request<RepairRecord>(`/api/repairs/${seg(domain)}`)
  }

  /** Apply what the ladder proposed, or the person's own correction when the proposal was only a lead. */
  acceptRepair(domain: string, req: RepairReviewRequest = {}): Promise<RepairRecord> {
    return this.request<RepairRecord>(`/api/repairs/${seg(domain)}/accept`, {
      method: 'POST',
      body: JSON.stringify(req),
    })
  }

  /** A no on a queued repair; a reversal on an applied one. */
  rejectRepair(domain: string): Promise<RepairRecord> {
    return this.request<RepairRecord>(`/api/repairs/${seg(domain)}/reject`, { method: 'POST', body: '{}' })
  }

  /** The queue across every job, one page at a time. */
  listAllReviews(
    filters: ReviewListFilters = {},
    cursor: string | null = null,
    limit: number = DEFAULT_PAGE_SIZE,
  ): Promise<Page<ReviewItem>> {
    return this.request<Page<ReviewItem>>(
      `/api/reviews${queryString({ ...filters, limit, cursor: cursor ?? undefined })}`,
    )
  }

  /** One page of a job's review queue. The status is already served. */
  listReviews(
    jobId: string,
    status: ReviewItem['status'] = 'open',
    cursor: string | null = null,
    limit: number = DEFAULT_PAGE_SIZE,
  ): Promise<Page<ReviewItem>> {
    return this.request<Page<ReviewItem>>(
      `/api/jobs/${seg(jobId)}/reviews${queryString({ status, limit, cursor: cursor ?? undefined })}`,
    )
  }

  /**
   * Accept and reject are separate routes because they are not symmetrical: a
   * rejection is also remembered against the company domain, so re-running the
   * same list never buys the same wrong person twice.
   */
  /** Run the automated reviewer over this job's open queue, one bounded batch. */
  autoReview(jobId: string, limit = 100): Promise<AutoReviewResult> {
    return this.request<AutoReviewResult>(`/api/jobs/${seg(jobId)}/reviews/auto?limit=${limit}`, {
      method: 'POST',
    })
  }

  decideReview(jobId: string, sortKey: string, decision: 'accept' | 'reject'): Promise<DecidedReview> {
    // The sort key is `rowKey#linkedinUrl` and carries both '/' and '#'. Even
    // percent-encoded it does not survive the path: CloudFront normalises the
    // escapes before forwarding, so API Gateway sees a different path and
    // answers 404. It travels in the body instead.
    return this.request<DecidedReview>(`/api/reviews/${seg(jobId)}/${decision}`, {
      method: 'POST',
      body: JSON.stringify({ sortKey }),
    })
  }

  // -- exports --

  createExport(req: CreateExportRequest): Promise<ExportRecord> {
    return this.request<ExportRecord>('/api/exports', { method: 'POST', body: JSON.stringify(req) })
  }

  getExport(exportId: string): Promise<ExportRecord> {
    return this.request<ExportRecord>(`/api/exports/${seg(exportId)}`)
  }
}

function errorMessage(status: number, body: unknown): string {
  if (typeof body === 'object' && body !== null) {
    const record = body as Record<string, unknown>
    const message = record['message'] ?? record['error']
    if (typeof message === 'string' && message.length > 0) {
      const issues = validationIssues(record['details'])
      return issues ? `${message}: ${issues}` : message
    }
  }
  if (typeof body === 'string' && body.length > 0 && body.length < 300) return body
  if (status === 401) return 'Your session has expired. Sign in again.'
  if (status === 403) return 'You do not have access to this.'
  if (status === 404) return 'Not found.'
  return `Request failed (${status}).`
}

/**
 * Every schema rejection on the API answers the same opaque "Request failed
 * validation" and puts the offending field in `details.issues`. Without this the
 * settings form and the wizard can only tell a user that *something* was wrong,
 * which is not enough to fix a capped number they typed one digit too many into.
 */
function validationIssues(details: unknown): string | null {
  if (typeof details !== 'object' || details === null) return null
  const raw = (details as { issues?: unknown }).issues
  if (!Array.isArray(raw)) return null

  const parts: string[] = []
  for (const issue of raw.slice(0, 4)) {
    if (typeof issue !== 'object' || issue === null) continue
    const { path, message } = issue as { path?: unknown; message?: unknown }
    if (typeof message !== 'string' || message.length === 0) continue
    parts.push(typeof path === 'string' && path.length > 0 ? `${path} — ${message}` : message)
  }
  return parts.length > 0 ? parts.join('; ') : null
}

/**
 * A rejected confirm still carries the report that explains the rejection: the
 * handler answers 400 with `{ error, validation }` when no row is usable. The
 * review step needs that body to say *which* rows are blocked, rather than
 * showing a bare "No usable rows".
 */
export function validationFromError(error: unknown): ValidationReport | null {
  if (!(error instanceof ApiError)) return null
  const detail = error.detail
  if (typeof detail !== 'object' || detail === null) return null
  const report = (detail as { validation?: unknown }).validation
  if (typeof report !== 'object' || report === null) return null
  return report as ValidationReport
}
